import type { AgChartOptions } from "ag-charts-community";
import { useMemo } from "react";

import { Chart } from "@web-app/components";

import ChartWrapper from "./ChartWrapper";
import { formatAxisValue } from "./formatAxisValue";

interface BarChartData {
  axisLabels?: {
    x?: string;
    y?: string;
  };
  datasets: Array<{
    backgroundColor?: string[];
    data: number[];
    label: string;
  }>;
  labels: string[];
}

interface BarChartProps {
  data: BarChartData;
  direction?: "horizontal" | "vertical";
  title: string;
}

export default function BarChart({
  data,
  title,
  direction = "vertical",
}: BarChartProps) {
  const chartData = useMemo(() => {
    if (!data?.labels || !data?.datasets?.length) return [];

    return data.labels.map((label, index) => {
      const dataPoint: Record<string, number | string> = { category: label };
      data.datasets.forEach((dataset, datasetIndex) => {
        if (datasetIndex === 0) {
          dataPoint.value = dataset.data[index] || 0;
        } else {
          dataPoint[`value${datasetIndex}`] = dataset.data[index] || 0;
        }
      });
      return dataPoint;
    });
  }, [data]);

  const chartOptions: AgChartOptions = useMemo(
    () => ({
      axes: [
        {
          position: direction === "horizontal" ? "left" : "bottom",
          title: {
            enabled: true,
            text: data?.axisLabels?.x || "Category",
          },
          type: "category",
        },
        {
          label: {
            formatter: (params) => formatAxisValue(params.value),
          },
          position: direction === "horizontal" ? "bottom" : "left",
          title: {
            enabled: true,
            text: data?.axisLabels?.y || data?.datasets?.[0]?.label || "Value",
          },
          type: "number",
        },
      ],
      data: chartData,
      series:
        data?.datasets?.map((dataset, index) => ({
          direction,
          type: "bar" as const,
          xKey: "category",
          yKey: index === 0 ? "value" : `value${index}`,
          yName: dataset.label || `Series ${index + 1}`,
        })) || [],
      tooltip: { range: "nearest" },
    }),
    [chartData, data, direction],
  );

  return (
    <ChartWrapper title={title}>
      <Chart options={chartOptions} isLoading={false} />
    </ChartWrapper>
  );
}
